import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBase } from "../lib/apiBase";

function Reset() {
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [mode, setMode] = useState("request");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const apiBase = useMemo(() => getApiBase(), []);

  const canSubmit = email.trim() && !isBusy;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim()) {
      setError("Please enter your email address.");
      setStatus("");
      return;
    }

    if (mode === "confirm" && (!resetToken || !newPassword)) {
      setError("Please enter the reset token and a new password.");
      setStatus("");
      return;
    }

    setIsBusy(true);
    setError("");
    setStatus(mode === "confirm" ? "Updating password..." : "Sending reset email...");

    try {
      const payload =
        mode === "confirm"
          ? {
              email: email.trim().toLowerCase(),
              reset_token: resetToken.trim(),
              new_password: newPassword,
            }
          : { email: email.trim().toLowerCase() };

      const response = await fetch(`${apiBase}/auth/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Reset failed.");
      }

      if (mode === "confirm") {
        setStatus("Password updated. You can log in now.");
        setNewPassword("");
        setResetToken("");
      } else {
        setStatus("Reset email sent. Check your inbox for the token.");
      }
    } catch (err) {
      setStatus("");
      setError(err?.message || "Something went wrong.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login-head">
        <p className="eyebrow">Reset access</p>
        <h2>Reset your password</h2>
        <p className="subtle">
          Request a reset email, then enter the token to set a new password.
        </p>
      </div>

      <form className="form" onSubmit={handleSubmit}>
        <label className="field">
          <span>Email address</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </label>

        {mode === "confirm" ? (
          <>
            <label className="field">
              <span>Reset token</span>
              <input
                type="text"
                value={resetToken}
                onChange={(event) => setResetToken(event.target.value)}
                placeholder="Paste the token from your email"
              />
            </label>
            <label className="field">
              <span>New password</span>
              <div className="password-field">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="Create a new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  <svg
                    className="eye-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M2.5 12s3.6-6.5 9.5-6.5S21.5 12 21.5 12 17.9 18.5 12 18.5 2.5 12 2.5 12Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    {showNewPassword ? (
                      <path
                        d="M4 20L20 4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    ) : null}
                  </svg>
                </button>
              </div>
            </label>
          </>
        ) : null}

        <button type="submit" disabled={!canSubmit}>
          {isBusy
            ? mode === "confirm"
              ? "Updating password..."
              : "Sending email..."
            : mode === "confirm"
              ? "Update password"
              : "Send reset email"}
        </button>
      </form>

      <div className="link-row">
        {mode === "confirm" ? (
          <button
            type="button"
            className="link-button"
            onClick={() => setMode("request")}
          >
            Need a new token?
          </button>
        ) : (
          <button
            type="button"
            className="link-button"
            onClick={() => setMode("confirm")}
          >
            Already have a token?
          </button>
        )}
      </div>

      {status ? <div className="status">{status}</div> : null}
      {error ? <div className="error">{error}</div> : null}

      <div className="link-row">
        <span>Remembered your password?</span>
        <Link className="link" to="/login">
          Log in
        </Link>
      </div>
    </div>
  );
}

export default Reset;
